import { useMutation, useQueryClient } from '@tanstack/react-query';
import * as Sentry from '@sentry/react';

import { useNotification } from '@/components/notifications';
import { QUALIFICATION_WAVES_QUERY_KEY } from '@/hooks/queries/use-qualification-waves';
import { APPROVERS_QUERY_KEY } from '@/hooks/queries/use-approvers';
import { QUALIFICATION_BOARD_QUERY_KEY } from '@/hooks/queries/use-qualification-board';
import {
  ApprovalSide,
  ApproverList,
  QualificationWave,
  WaveInput,
  WavePatch,
  createWave,
  setApprovers,
  updateWave,
} from '@/services/workflow/qualification';

const report = (context: string, error: Error) => {
  Sentry.captureException(error, { extra: { context } });
};

export const useCreateWave = (onDone?: () => void) => {
  const queryClient = useQueryClient();
  const notification = useNotification();

  return useMutation<QualificationWave, Error, WaveInput>({
    mutationFn: createWave,
    onSuccess: (wave) => {
      queryClient.invalidateQueries({
        queryKey: [QUALIFICATION_WAVES_QUERY_KEY],
      });
      notification.success(`${wave.name} created`);
      onDone?.();
    },
    onError: (error) => {
      notification.error(error.message || 'Could not create the wave');
      report('create-wave', error);
    },
  });
};

export const useUpdateWave = (onDone?: () => void) => {
  const queryClient = useQueryClient();
  const notification = useNotification();

  return useMutation<
    QualificationWave,
    Error,
    { waveId: string; patch: WavePatch }
  >({
    mutationFn: ({ waveId, patch }) => updateWave(waveId, patch),
    onSuccess: (wave) => {
      queryClient.invalidateQueries({
        queryKey: [QUALIFICATION_WAVES_QUERY_KEY],
      });
      // Amending a wave can change what the pipeline is waiting on: turning
      // client sign-off off settles any attempt already holding Capitol
      // approval, which moves rows on the board with nothing else happening.
      queryClient.invalidateQueries({
        queryKey: [QUALIFICATION_BOARD_QUERY_KEY],
      });
      notification.success(`${wave.name} updated`);
      onDone?.();
    },
    onError: (error) => {
      notification.error(error.message || 'Could not update the wave');
      report('update-wave', error);
    },
  });
};

export const useSetApprovers = (onDone?: () => void) => {
  const queryClient = useQueryClient();
  const notification = useNotification();

  return useMutation<
    ApproverList,
    Error,
    {
      client: string;
      region: string;
      side: ApprovalSide;
      emails: string[];
      waveId?: string;
      useDefault?: boolean;
    }
  >({
    mutationFn: ({ client, region, side, emails, waveId, useDefault }) =>
      setApprovers(client, region, side, emails, waveId, useDefault),
    onSuccess: (_emails, { side }) => {
      queryClient.invalidateQueries({ queryKey: [APPROVERS_QUERY_KEY] });
      notification.success(
        `${side === 'capitol' ? 'Capitol' : 'Client'} approvers saved`,
      );
      onDone?.();
    },
    onError: (error) => {
      notification.error(error.message || 'Could not save the approvers');
      report('set-approvers', error);
    },
  });
};
